import React, { useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ConversationTimelineItem } from '@/client/api/whatsapp/get-conversation.query';
import { fetchWhatsAppMediaBlob } from '@/client/api/whatsapp/media-download';
import MessageText from '@/client/components/admin/MessageText/MessageText';

const MEDIA_TYPES = ['image', 'video', 'document', 'audio', 'sticker'];

/** The media kind of a message, or null when it is plain text. */
export const mediaKindOf = (item: ConversationTimelineItem): string | null => {
  const kind = item.direction === 'inbound' ? item.message_type : (item.payload?.type as string | undefined);
  return kind && MEDIA_TYPES.includes(kind) ? kind : null;
};

/**
 * A file attached to a message.
 *
 * Received on a Meta number: there is a media id, and the file is fetched
 * through the server when asked for. Received on the UltraMsg number: there is
 * a public link, so images show inline. Sent by us: only the name is kept.
 */
const MessageAttachment: React.FC<{ item: ConversationTimelineItem }> = ({ item }) => {
  const [loading, setLoading] = useState(false);
  const kind = mediaKindOf(item);
  if (!kind) return null;

  const outbound = item.direction === 'outbound';
  const media = (outbound ? (item.payload?.[kind] as Record<string, unknown> | undefined) : item.data) || {};
  const name = (media.filename as string) || kind;
  const caption = media.caption as string | undefined;
  const url = (outbound ? media.link : media.media_url) as string | undefined;
  const mediaId = outbound ? undefined : (media.media_id as string | undefined);

  const open = async () => {
    if (!mediaId) return;
    setLoading(true);
    try {
      const blob = await fetchWhatsAppMediaBlob(mediaId);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      // Images and PDFs open in a tab; anything else downloads under its name.
      if (/^(image\/|application\/pdf)/.test(blob.type)) link.target = '_blank';
      else link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load the file');
    } finally {
      setLoading(false);
    }
  };

  const chip = 'inline-flex items-center gap-2 text-sm underline underline-offset-2';

  return (
    <div className="space-y-1">
      {url && kind === 'image' && (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={caption || 'Image'} className="max-h-60 rounded-md" />
        </a>
      )}
      {url && kind !== 'image' && (
        <a href={url} target="_blank" rel="noreferrer" className={chip}>
          <Download className="w-4 h-4" /> {name}
        </a>
      )}
      {!url && mediaId && (
        <button onClick={open} disabled={loading} className={chip}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {kind === 'image' ? 'View image' : `Download ${name}`}
        </button>
      )}
      {!url && !mediaId && (
        <span className="inline-flex items-center gap-2 text-sm">
          <FileText className="w-4 h-4" />
          {outbound ? name : `${name} (file not available)`}
        </span>
      )}
      {caption && <MessageText text={caption} tone={outbound ? 'dark' : 'light'} />}
    </div>
  );
};

export default MessageAttachment;
